import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useRedeemHandoff } from "../../useQuery";

const readUserDetail = () => {
  try {
    return JSON.parse(localStorage.getItem("userDetail"));
  } catch {
    return null;
  }
};

/**
 * `/api` is opened cross-origin from the new UI, which cannot write to this
 * origin's localStorage. It mints a single-use `?handoff=` code instead
 * (my-app-v2 Sidebar.tsx -> POST /KeyLogging/handoff); this redeems it into the
 * `userDetail` every /api screen reads.
 */
const ApiHandoffGate = ({ children }) => {
  // Captured once: the code is stripped from the URL as soon as it is spent.
  const [code] = useState(() => new URLSearchParams(window.location.search).get("handoff"));
  const [status, setStatus] = useState(code ? "redeeming" : "ready");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const spent = useRef(false);

  const { mutate: redeem } = useRedeemHandoff({
    onSuccess: (data) => {
      if (!data?.token) {
        setStatus("failed");
        return;
      }
      localStorage.setItem(
        "userDetail",
        JSON.stringify({
          token: data.token,
          companyid: data.companyId,
          company: { brandLogo: data.brandLogo },
        }),
      );
      setStatus("ready");
    },
    onError: () => setStatus("failed"),
  });

  useEffect(() => {
    if (!code || spent.current) return;
    spent.current = true;
    // Single-use code: take it out of the URL so a reload cannot replay it.
    navigate(pathname, { replace: true });
    redeem({ code });
  }, [code, pathname, navigate, redeem]);

  if (status === "redeeming") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
          <h3 className="text-xl font-semibold">Signing in…</h3>
        </div>
      </div>
    );
  }

  if (status === "failed" || !readUserDetail()?.token) {
    return <Navigate to="/LoginPage" replace />;
  }

  return children;
};

export default ApiHandoffGate;
